// textOverlay — date stamp + venue + city in the bottom-right corner, in the
// canon Special Elite "stamp" font (loaded globally via Google Fonts in the
// app shell). Right-aligned, three lines max.
//
// Color picking: probe the average luminance of the bottom-right region. On
// bright photos use --orange (#f06b28); on dark, use --paper (#fffaf0). The
// goal is legibility without ever being plain black/white.

import { type FilterContext, luma } from './_helpers'

export type TextOverlayOpts = {
  timestamp: Date
  venueName?: string
  city?: string
}

const FONT_SIZE = 18
const PADDING = 16
const LINE_HEIGHT = 22
const FONT = `${FONT_SIZE}px 'Special Elite', monospace`
const COLOR_LIGHT = '#fffaf0' // --paper
const COLOR_DARK = '#f06b28' // --orange
const LUMA_THRESHOLD = 128

export function textOverlay(c: FilterContext, opts: TextOverlayOpts): void {
  const lines = formatLines(opts)
  if (lines.length === 0) return

  // Probe a ~120×80 region in the bottom-right for average luminance, so we
  // know whether to use the dark or light stamp color.
  const probeW = Math.min(120, c.width)
  const probeH = Math.min(80, c.height)
  const probeX = Math.max(0, c.width - probeW)
  const probeY = Math.max(0, c.height - probeH)
  const probe = c.ctx.getImageData(probeX, probeY, probeW, probeH)
  const avgLuma = averageLuma(probe)
  const color = avgLuma > LUMA_THRESHOLD ? COLOR_DARK : COLOR_LIGHT

  c.ctx.font = FONT
  c.ctx.fillStyle = color
  c.ctx.textAlign = 'right'
  c.ctx.textBaseline = 'bottom'

  const x = c.width - PADDING
  const startY = c.height - PADDING - LINE_HEIGHT * (lines.length - 1)
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]
    if (line === undefined) continue
    c.ctx.fillText(line, x, startY + LINE_HEIGHT * i)
  }
}

function formatLines(opts: TextOverlayOpts): string[] {
  const lines: string[] = []
  lines.push(formatDate(opts.timestamp))
  if (opts.venueName) lines.push(`@ ${opts.venueName}`)
  if (opts.city) lines.push(`· ${opts.city}`)
  return lines
}

function formatDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const yy = String(d.getFullYear() % 100).padStart(2, '0')
  return `${mm}·${dd}·${yy}`
}

function averageLuma(data: ImageData): number {
  const d = data.data
  let sum = 0
  let count = 0
  // Sample every 4th pixel (16 bytes) to keep this fast on large probes.
  for (let i = 0; i < d.length; i += 16) {
    const r = d[i] ?? 0
    const g = d[i + 1] ?? 0
    const b = d[i + 2] ?? 0
    sum += luma(r, g, b)
    count += 1
  }
  return count === 0 ? 0 : sum / count
}
